import { ReactNode, createContext, useContext } from 'react';

import { useRotatingLoadingMessages } from '@components/rotating-loading-messages';
import { useScopedTranslate } from '@locale/translate';

import { useDuplicateJourneyAction, useCreateJourneyAction } from 'api/journey-actions/hooks';
import { CreateJourneyActionData } from 'api/types';

import { useDeleteJourneyAction } from './hooks/use-delete-journey-action';
import { JourneyActionBasic } from './node-types/common';

type JourneyActionsContextType = {
  handleDuplicate: (shortId: string) => void;
  handleDelete: (action: JourneyActionBasic) => Promise<void>;
  handleCreate: (data: CreateJourneyActionData) => void;
};

export const JourneyBuilderActionsContext = createContext<JourneyActionsContextType | undefined>(
  undefined,
);

export const JourneyBuilderActionsProvider = ({ children }: { children: ReactNode }) => {
  const duplicateMutation = useDuplicateJourneyAction();
  const createMutation = useCreateJourneyAction();
  const { t } = useScopedTranslate(locale);
  const { deleteAction: handleDelete } = useDeleteJourneyAction();

  const handleDuplicate = (shortId: string) => {
    duplicateMutation.mutate(shortId);
  };

  const handleCreate = (data: CreateJourneyActionData) => {
    createMutation.mutate(data);
  };

  useRotatingLoadingMessages({
    isEnabled: duplicateMutation.isLoading,
    firstMessage: t('duplicatingAction'),
  });

  return (
    <JourneyBuilderActionsContext.Provider value={{ handleDuplicate, handleDelete, handleCreate }}>
      {children}
    </JourneyBuilderActionsContext.Provider>
  );
};

export const useJourneyBuilderActionsContext = () => {
  const context = useContext(JourneyBuilderActionsContext);
  if (!context) {
    throw new Error('useJourneyActionsContext must be used within a JourneyActionsProvider');
  }
  return context;
};

const locale = {
  en: {
    duplicatingAction: 'Duplicating...',
  },
};
